// Project Navigation Service
// Handles navigation to project details from various sources including iframes

import { iframeMessageService, ProjectNavigationMessage } from './iframe-message-service';
import store from '@/store';
import router from '@/router';

class ProjectNavigationService {
  constructor() {
    this.setupMessageHandlers();
  }

  /**
   * Set up message handlers for iframe communication
   */
  private setupMessageHandlers(): void {
    // Handle project navigation requests from iframes
    iframeMessageService.on('NAVIGATE_TO_PROJECT', this.handleProjectNavigation.bind(this));
    
    // Handle generic navigation requests
    iframeMessageService.on('NAVIGATE_TO_ROUTE', this.handleRouteNavigation.bind(this));
  }

  /**
   * Handle project navigation from iframe messages
   */
  private async handleProjectNavigation(message: ProjectNavigationMessage): Promise<void> {
    const { 
      projectId, 
      action = 'details', 
      context, 
      project, 
      projectDetails, 
      projectData, 
      urlParams 
    } = message.data;

    try {
      // Maintain backward compatibility: if original projectData exists, use it
      // Otherwise, derive data from the new structure
      let dataToUse = projectData; // Original behavior preserved
      
      if (!dataToUse) {
        // New functionality: use projectDetails if available
        if (projectDetails) {
          dataToUse = projectDetails;
        } else if (project) {
          // Transform project data to match expected format
          // Use flexible typing since this is external data
          dataToUse = {
            intProjectID: project.id,
            id: project.id,
            projectRefNumber: project.refNumber,
            status: project.status,
            businessName: project.businessName,
            location: project.location,
            description: project.jobDescription,
            technician: project.technician,
            client: project.client || project.businessName,
            start: project.start,
            end: project.end,
            dueDate: project.end ? new Date(project.end).toISOString().split('T')[0] : null,
            // Additional fields from new data structure
            ...(project.title && { title: project.title }),
            ...(project.jobDescription && { jobDescription: project.jobDescription }),
            ...(project.technicianStatus && { technicianStatus: project.technicianStatus }),
            ...(project.contact && { contact: project.contact }),
            ...(project.color && { color: project.color }),
            ...(project.start && { startDate: project.start }),
            ...(project.end && { endDate: project.end })
          } as any; // Use any for flexibility with external data
        }
      }

      // If any project data is provided in the message, use it directly
      if (dataToUse) {
        await this.navigateToProjectWithData(projectId, dataToUse, action, context, urlParams);
      } else {
        // Fallback to fetching data from API (original behavior)
        await this.navigateToProject(projectId, action, context);
      }
      
      // Send acknowledgment back to iframe
      iframeMessageService.sendAcknowledgment('*', message);
    } catch (error) {
      // Error handled silently to prevent deployment issues
    }
  }

  /**
   * Handle generic route navigation
   */
  private async handleRouteNavigation(message: any): Promise<void> {
    const { route, params } = message.data;

    try {
      if (params) {
        await router.push({ name: route, params });
      } else {
        await router.push(route);
      }

      // Send acknowledgment back to iframe
      iframeMessageService.sendAcknowledgment('*', message);
    } catch (error) {
      // Error handled silently to prevent deployment issues
    }
  }

  /**
   * Navigate to project details using provided data (for iframe messages with project data)
   */
  public async navigateToProjectWithData(
    projectId: string | number,
    projectData: any, 
    action: string = 'details',
    context?: string,
    urlParams?: any
  ): Promise<void> {
    try {
      // Convert projectId to number if it's a string
      const numericProjectId = typeof projectId === 'string' ? parseInt(projectId, 10) : projectId;

      // For iframe contexts (calendar/scheduler), navigate to board route to enable sidepanel
      if (context === 'calendar' || context === 'scheduler') {
        // Preserve iframe state while navigating to a route that supports sidepanels
        const iframeProperties = store.getters.getIframeProperties;
        
        // Ensure iframe properties are preserved
        if (!iframeProperties.showIframe) {
          store.dispatch('setApplicationIframe', {
            showIframe: true,
            iframeUrl: 'http://localhost:3000/',
            friendlyUrl: '/calendar',
          });
        }

        // Navigate to the calendar child route so the side panel component loads
        const route = { 
          name: 'calendar-project-details', 
          params: { id: numericProjectId.toString() } 
        };

        // Navigate to the calendar child route to enable the sidepanel
        if (router.currentRoute.name !== 'calendar-project-details' || 
          router.currentRoute.params.id !== numericProjectId.toString()) {
          await router.push(route);
        }

        // Open side panel with the provided data
        // Add a small delay to ensure router navigation completes
        setTimeout(() => {
          this.showProjectSidePanel(projectData, route, urlParams);
        }, 100);
      } else {
        // For non-iframe contexts, use the original navigation behavior
        // Close any existing iframe view
        store.dispatch('setApplicationIframe', {
          showIframe: false,
          iframeUrl: '',
          friendlyUrl: '',
        });

        // Determine the appropriate route based on context
        let routeName = 'projects-details';
        let routePath = `/projects/details/${numericProjectId}`;

        // Create route object
        const route = { 
          name: routeName, 
          params: { id: numericProjectId.toString() } 
        };

        // Update the router if we're not already on this route
        if (router.currentRoute.path !== routePath) {
          await router.push(route);
        }

        // For projects-details route, don't automatically open side panel
        // The side panel should only open for board routes or when explicitly requested
      }

    } catch (error) {
      // Error handled silently to prevent deployment issues
      throw error;
    }
  }

  /**
   * Navigate to project details and open side panel
   * Modified to keep iframe visible while opening sidebar
   */
  public async navigateToProject(
    projectId: string | number, 
    action: string = 'details',
    context?: string
  ): Promise<void> {
    try {
      // Convert projectId to number if it's a string
      const numericProjectId = typeof projectId === 'string' ? parseInt(projectId, 10) : projectId;

      // For iframe contexts (calendar/scheduler), navigate to board route to enable sidepanel
      if (context === 'calendar' || context === 'scheduler') {
        // Preserve iframe state while navigating to a route that supports sidepanels
        const iframeProperties = store.getters.getIframeProperties;
        
        // Ensure iframe properties are preserved
        if (!iframeProperties.showIframe) {
          store.dispatch('setApplicationIframe', {
            showIframe: true,
            iframeUrl: 'http://localhost:3000/',
            friendlyUrl: '/calendar',
          });
        }

        // Get project data 
        const projectData = await this.fetchProjectData(numericProjectId);

        if (projectData) {
          // Navigate to the calendar child route so the side panel component loads
          const route = { 
            name: 'calendar-project-details', 
            params: { id: numericProjectId.toString() } 
          };

          // Navigate to the calendar child route to enable the sidepanel
          if (router.currentRoute.name !== 'calendar-project-details' || 
            router.currentRoute.params.id !== numericProjectId.toString()) {
            await router.push(route);
          }

          // Open side panel with fetched data
          // Add a small delay to ensure router navigation completes
          setTimeout(() => {
            this.showProjectSidePanel(projectData, route);
          }, 100);
        } else {
          // Project not found - handled silently to prevent deployment issues
        }
      } else {
        // For non-iframe contexts, use the original navigation behavior
        // Close any existing iframe view
        store.dispatch('setApplicationIframe', {
          showIframe: false,
          iframeUrl: '',
          friendlyUrl: '',
        });

        // Determine the appropriate route based on context
        let routeName = 'projects-details';
        let routePath = `/projects/details/${numericProjectId}`;

        // Create route object
        const route = { 
          name: routeName, 
          params: { id: numericProjectId.toString() } 
        };

        // Update the router if we're not already on this route
        if (router.currentRoute.path !== routePath) {
          await router.push(route);
        }

        // Get project data and show sidebar
        const projectData = await this.fetchProjectData(numericProjectId);
        // For projects-details route, don't automatically open side panel
        // The side panel should only open for board routes or when explicitly requested
      }

    } catch (error) {
      // Error handled silently to prevent deployment issues
      throw error;
    }
  }

  /**
   * Fetch project data from API
   * Returns raw project data - formatting will be handled by showProjectSidePanel
   */
  private async fetchProjectData(projectId: number): Promise<any> {
    try {
      // Import ProjectServices to get raw project data
      const { default: ProjectServices } = await import('@/services/projects');
      const projectServices = new ProjectServices();
      
      // Fetch raw project details - formatting will be done later
      const project = await projectServices.getProjectDetails(projectId);
      return project;
    } catch (error) {
      // Error handled silently to prevent deployment issues
      return null;
    }
  }

  /**
   * Format raw project data to include display fields expected by ProjectController
   * Handles both API data and iframe calendar data
   */
  private formatProjectData(project: any): any {
    // Check if data is already formatted to avoid double-formatting
    if (project.displayDueDate && project.displayCreatedDate && project.displayDueDateStatus) {
      return project;
    }

    // Import moment for date formatting
    const moment = require('moment');

    // Helper function to format dates
    const formatDate = (dateValue: any) => {
      if (!dateValue) return 'N/A';
      try {
        return moment(dateValue).format('DD-MMM-YYYY');
      } catch (e) {
        return 'N/A';
      }
    };

    // Helper function to determine due date status
    const getDueDateStatus = (dueDate: any) => {
      if (!dueDate) return 'No Due Date';
      try {
        const due = moment(dueDate);
        const now = moment();
        const diffDays = due.diff(now, 'days');

        if (diffDays < 0) return 'Overdue';
        if (diffDays === 0) return 'Due Today';
        if (diffDays <= 7) return 'Due This Week';
        return 'On Schedule';
      } catch (e) {
        return 'Unknown';
      }
    };

    // Handle different data sources - iframe vs API
    // Iframe data uses 'start'/'end', API uses 'projectStartDate'/'projectEndDate'
    const startDateField = project.projectStartDate || project.start || project.startDate;
    const endDateField = project.projectEndDate || project.end || project.endDate;
    const dueDateField = project.dueDate;

    // For created date, iframe data might not have it, so we'll use a fallback
    const createdDateField = project.createdDate || project.utcCreatedDate || null;

    // Add formatted fields expected by ProjectController
    const formattedData = {
      ...project,
      displayDueDate: formatDate(dueDateField),
      displayCreatedDate: formatDate(createdDateField),
      displayStartDate: formatDate(startDateField),
      displayEndDate: formatDate(endDateField),
      displayDueDateStatus: getDueDateStatus(dueDateField),
      createdBy: project.createdBy || 'Unknown'
    };

    return formattedData;
  }

  /**
   * Show project side panel using ApplicationService pattern
   * Enhanced to support URL parameters while preserving existing functionality
   */
  private showProjectSidePanel(projectData: any, route: any, urlParams?: any): void {
    try {
      // CRITICAL: Always format project data to ensure display fields are present
      // This handles both API data and iframe data consistently
      let dataToPass = this.formatProjectData(projectData);
      
      // Enhancement: if urlParams are provided, merge them into the data
      if (urlParams) {
        dataToPass = {
          ...dataToPass,
          urlParams
        };
      }

      // For router-based sidepanels (like calendar route), use Vuex store directly
      if (route.name === 'calendar-project-details') {
        // Get the existing panelObj from the store to trigger the slideout panel
        const currentSidePanel = store.getters.getSidePanel;
        
        // Set the sidepanel data and open state directly in the store
        store.dispatch('setSidePanel', {
          isOpen: true,
          isSidePanelFrame: false,
          name: 'MainSidePanel',
          data: dataToPass,
          // panelObj is preserved automatically by the store merge logic
        });

        // Show the slideout panel if panelObj exists
        if (currentSidePanel.panelObj && currentSidePanel.panelObj.show) {
          currentSidePanel.panelObj.show();
        }
      } else {
        // For non-router based sidepanels, use the original ApplicationService method
        import('@/services/application').then(({ default: ApplicationService }) => {
          const app = new ApplicationService();
          app.showSidePanel(null, dataToPass, route);
        });
      }
    } catch (error) {
      // Error handled silently to prevent deployment issues
    }
  }

  /**
   * Navigate to project from external source (public method)
   */
  public async navigateToProjectFromExternal(
    projectId: string | number,
    context: string = 'external'
  ): Promise<void> {
    await this.navigateToProject(projectId, 'details', context);
  }
}

// Export singleton instance
export const projectNavigationService = new ProjectNavigationService();
export default projectNavigationService;
